import {
	AccountCircle,
	AssistantDirection,
	ConfirmationNumber,
	Email,
	LocationCity,
	LocationOn,
	Markunread,
	MyLocation,
	Notes,
	Phone,
	Subtitles,
} from '@mui/icons-material';
import { Checkbox, Divider, FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import { useGlobalStyles } from 'Components/Common/global/global';
import { ContentSkeleton } from 'Components/Deposits/CreateEditDepositWithDonations/Content';
import ProvinceAutocomplete from 'Components/Province';
import { Diocese, StateType } from 'contexts/intialstates/Types';
import useDioceseActions from 'hooks/useDioceseActions';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

export interface DioceseValues {
	dioceseName: string;
	chancellor: string;
	streetLine1: string;
	streetline2: string;
	city: string;
	provinceId?: number;
	province: string;
	postalCode: string;
	country: string;
	contactName: string;
	phone: string;
	fax: string;
	email: string;
	language: string;
	useEmail: boolean;
	useEFile: boolean;
	magazine: boolean;
	code?: number;
	bishop: string;
}

export const leftFieldStack = {
	display: 'flex',
	flexDirection: { sm: 'row', xs: 'column' },
	justifyContent: { xs: 'center', sm: 'flex-start' },
	mb: 2,
	height: '100%',
};
export const rightFieldStack = {
	display: 'flex',
	flexDirection: { sm: 'row', xs: 'column' },
	justifyContent: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
	mb: 2,
};
export const centerFieldStack = {
	display: 'flex',
	flexDirection: { sm: 'row', xs: 'column' },
	justifyContent: { xs: 'center', sm: 'flex-end', md: 'flex-start' },
	mb: 2,
};

interface P {
	initial: DioceseValues;
	setInitial: React.Dispatch<React.SetStateAction<DioceseValues>>;
	formErrors: string[];
	setFormErrors: React.Dispatch<React.SetStateAction<string[]>>;
}

const CreateEditDiocese = ({ initial, setInitial, formErrors, setFormErrors }: P) => {
	const global = useGlobalStyles();
	const { id } = useParams();
	const { getDioceseByID } = useDioceseActions();

	const [diocese, setDiocese] = React.useState<StateType<Diocese>>({
		data: null,
		error: null,
		loading: false,
	});

	useEffect(() => {
		if (!id) return;
		getDioceseByID({ setDiocese, diocese, id: parseInt(id) });
	}, [id]);

	useEffect(() => {
		if (!diocese?.data) return;
		const {
			code,
			dioceseName,
			chancellor,
			streetLine1,
			streetline2,
			city,
			province,
			postalCode,
			country,
			contactName,
			phone,
			fax,
			email,
			language,
			useEmail,
			useEFile,
			magazine,
			bishop,
		} = diocese?.data;
		setInitial({
			code,
			dioceseName,
			chancellor,
			streetLine1,
			streetline2,
			city,
			province: province?.provinceName ?? '',
			provinceId: province?.id,
			postalCode,
			country,
			contactName,
			phone,
			fax,
			email,
			language,
			useEmail,
			useEFile,
			magazine,
			bishop,
		});
	}, [diocese]);

	const handleChange = (field: string, value: string | number | boolean) => {
		setInitial({
			...initial,
			[field]: value,
		});
		if (formErrors.includes(field)) {
			setFormErrors(formErrors.filter((item) => item !== field));
		}
	};

	return (
		<Stack>
			{diocese?.loading ? (
				<ContentSkeleton />
			) : (
				<>
					<Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, mt: 2 }}>Basic Detail</Typography>
					<Divider />
					<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<span className={global.formLabelIcon}>#</span>
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Code
									</Typography>
								</Grid>
							</Grid>
							<Stack direction="column">
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Code"
									value={initial.code}
									disabled
									className={global.disabledInput}
								/>
								<span style={{ color: 'var(--mainColor)', fontSize: 12, marginTop: 2 }}>
									This is an auto-generated code
								</span>
							</Stack>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Subtitles className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Diocese Name
									</Typography>
								</Grid>
							</Grid>
							<Stack>
								<TextField
									size="small"
									fullWidth
									variant="outlined"
									placeholder="Diocese Name"
									value={initial.dioceseName}
									onChange={(e) => {
										handleChange('dioceseName', e.target.value);
									}}
								/>
								{formErrors.includes('dioceseName') && (
									<span className={global.errorText}>Diocese name is required</span>
								)}
							</Stack>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Notes className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Chancellor
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Chancellor"
								value={initial.chancellor}
								onChange={(e) => {
									handleChange('chancellor', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Notes className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Bishop
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Bishop"
								value={initial.bishop}
								onChange={(e) => {
									handleChange('bishop', e.target.value);
								}}
							/>
						</Grid>
					</Grid>
					<Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, mt: 2 }}>
						Contact Detail
					</Typography>
					<Divider />
					<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<AccountCircle className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Contact Name
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Contact Name"
								value={initial.contactName}
								onChange={(e) => {
									handleChange('contactName', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Phone className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Phone
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Phone"
								value={initial.phone}
								type="number"
								onChange={(e) => {
									handleChange('phone', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Phone className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Fax
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Fax"
								value={initial.fax}
								onChange={(e) => {
									handleChange('fax', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Email className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Email
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Email"
								value={initial.email}
								onChange={(e) => {
									handleChange('email', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<ConfirmationNumber className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Language
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Language"
								value={initial.language}
								onChange={(e) => {
									handleChange('language', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<FormControlLabel
								name="useEFile"
								value="top"
								control={
									<Checkbox
										checked={initial.useEFile}
										onChange={(e) => handleChange('useEFile', e.target.checked)}
									/>
								}
								label="Use EFile"
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<FormControlLabel
								name="useEmail"
								value="top"
								control={
									<Checkbox
										checked={initial.useEmail}
										onChange={(e) => handleChange('useEmail', e.target.checked)}
									/>
								}
								label="Use Email"
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<FormControlLabel
								name="magazine"
								value="top"
								control={
									<Checkbox
										checked={initial.magazine}
										onChange={(e) => handleChange('magazine', e.target.checked)}
									/>
								}
								label="Magazine"
							/>
						</Grid>
					</Grid>
					<Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 600, mt: 2 }}>
						Address Details
					</Typography>
					<Divider />
					<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 4 }} mt={2}>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<LocationOn className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Street Line 1
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Streetline 1"
								value={initial.streetLine1}
								onChange={(e) => {
									handleChange('streetLine1', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<MyLocation className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Street Line 2
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Streetline 2"
								value={initial.streetline2}
								onChange={(e) => {
									handleChange('streetline2', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<LocationCity className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										City
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="City"
								value={initial.city}
								onChange={(e) => {
									handleChange('city', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<AssistantDirection className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Province
									</Typography>
								</Grid>
							</Grid>
							<ProvinceAutocomplete
								value={initial.province}
								handleChange={(val) => handleChange('provinceId', parseInt(val?.id))}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<Markunread className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Postal Code
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Postal Code"
								value={initial.postalCode}
								onChange={(e) => {
									handleChange('postalCode', e.target.value);
								}}
							/>
						</Grid>
						<Grid item xs={12} md={4} sm={6} mb={3} pr={{ lg: 5, md: 0 }}>
							<Grid container>
								<Grid item>
									<LocationCity className={global.formLabelIcon} />
								</Grid>
								<Grid item>
									<Typography variant="body2" className={global.formLabelText}>
										Country
									</Typography>
								</Grid>
							</Grid>
							<TextField
								size="small"
								fullWidth
								variant="outlined"
								placeholder="Country"
								value={initial.country}
								onChange={(e) => {
									handleChange('country', e.target.value);
								}}
							/>
						</Grid>
					</Grid>
				</>
			)}
		</Stack>
	);
};

export default CreateEditDiocese;
